import { type FC } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { AxisFinanceLogo } from './AxisFinanceLogo';

/**
 * PublicTopBar — shared top bar for the unauthenticated surface (login +
 * marketing pages). Logo + product name, nav links, and a Get Started CTA.
 *
 * On the login page, pass `onGetStarted` to start the Office 365 sign-in. On
 * marketing pages, omit it and the CTA links to /login.
 */

export interface PublicTopBarProps {
  onGetStarted?: () => void;
  isLoading?: boolean;
}

const NAV = [
  { label: 'Product', to: '/product' },
  { label: 'How it Works', to: '/how-it-works' },
  { label: 'Resources', to: '/resources' },
];

export const PublicTopBar: FC<PublicTopBarProps> = ({ onGetStarted, isLoading }) => (
  <header className="shrink-0 border-b border-outline-variant/60">
    <div className="max-w-6xl mx-auto w-full px-6 h-16 flex items-center justify-between">
      <Link to="/login" className="flex items-center gap-2.5 min-w-0">
        <AxisFinanceLogo height={26} />
        <span className="font-display text-label font-bold tracking-tight text-on-surface hidden sm:inline truncate">
          Agentic Loan Audit
        </span>
      </Link>

      <div className="flex items-center gap-6">
        <nav className="hidden md:flex items-center gap-6 text-caption font-medium text-on-surface-variant">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} className="hover:text-primary transition-colors">
              {n.label}
            </Link>
          ))}
        </nav>
        {onGetStarted ? (
          <button
            onClick={onGetStarted}
            disabled={isLoading}
            className="h-9 px-4 rounded-lg bg-primary text-on-primary text-caption font-semibold hover:bg-primary-container transition-colors disabled:opacity-60"
          >
            Get Started
          </button>
        ) : (
          <Link
            to="/login"
            className="h-9 px-4 inline-flex items-center gap-1.5 rounded-lg bg-primary text-on-primary text-caption font-semibold hover:bg-primary-container transition-colors"
          >
            Get Started
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        )}
      </div>
    </div>
  </header>
);

export default PublicTopBar;
